import { useState } from 'react';
import { useOffice, type Office } from '@/hooks/useOffice';
import { useAuth } from '@/hooks/useAuth';

export default function OfficeSwitcher() {
  const {
    offices,
    currentOfficeId,
    currentOffice,
    switchOffice,
    createOffice,
    renameOffice,
    deleteOffice,
  } = useOffice();
  const { profile } = useAuth();
  const canCreateOffice = profile?.role === 'admin';
  const isOwner = profile?.is_owner === true;

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<Office | null>(null);
  const [deleting, setDeleting] = useState<Office | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [deleteErr, setDeleteErr] = useState('');

  const reset = () => {
    setCreating(false);
    setRenaming(null);
    setName('');
    setErr('');
  };

  const submitCreate = async () => {
    if (busy) return;
    if (!name.trim()) {
      setErr('Please enter an office name');
      return;
    }
    setBusy(true);
    setErr('');
    const office = await createOffice(name);
    setBusy(false);
    if (office) {
      setName('');
      setCreating(false);
      setOpen(false);
    } else {
      setErr('Failed to create office');
    }
  };

  const startRename = (o: Office) => {
    setRenaming(o);
    setName(o.name);
    setErr('');
  };

  const submitRename = async () => {
    if (busy || !renaming) return;
    if (!name.trim()) {
      setErr('Please enter an office name');
      return;
    }
    setBusy(true);
    setErr('');
    const office = await renameOffice(renaming.id, name);
    setBusy(false);
    if (office) {
      setRenaming(null);
      setName('');
    } else {
      setErr('Failed to rename office');
    }
  };

  const confirmDelete = async () => {
    if (busy || !deleting) return;
    setBusy(true);
    setDeleteErr('');
    const ok = await deleteOffice(deleting.id);
    setBusy(false);
    if (ok) {
      setDeleting(null);
      setOpen(false);
    } else {
      setDeleteErr('Failed to delete office. It may still contain tapes or boxes.');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((o) => !o);
          reset();
        }}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 cursor-pointer transition whitespace-nowrap"
      >
        <i className="ri-building-2-line w-4 h-4 flex items-center justify-center text-slate-500 shrink-0" />
        <span className="flex-1 text-left truncate font-medium">
          {currentOffice?.name ?? 'Select office'}
        </span>
        <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-slate-400 shrink-0" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false);
              reset();
            }}
          />
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg py-1">
            <div className="max-h-56 overflow-y-auto">
              {offices.map((o) => {
                const active = o.id === currentOfficeId;
                const isRenaming = renaming?.id === o.id;
                return (
                  <div
                    key={o.id}
                    className={`flex items-center ${
                      active ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {isRenaming ? (
                      <div className="flex-1 px-2 py-1.5">
                        <input
                          autoFocus
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            setErr('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitRename();
                            if (e.key === 'Escape') {
                              setRenaming(null);
                              setName('');
                              setErr('');
                            }
                          }}
                          placeholder="Office name"
                          className="w-full text-sm px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 text-slate-700"
                        />
                        {err && <div className="text-[11px] text-red-600 mt-1">{err}</div>}
                        <div className="flex gap-2 mt-1.5">
                          <button
                            onClick={submitRename}
                            disabled={busy}
                            className="flex-1 px-2 py-1.5 text-xs rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            {busy ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setRenaming(null);
                              setName('');
                              setErr('');
                            }}
                            className="px-2 py-1.5 text-xs rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            switchOffice(o.id);
                            setOpen(false);
                          }}
                          className="flex-1 flex items-center gap-2 px-3 py-2 text-sm cursor-pointer whitespace-nowrap text-left"
                        >
                          <i
                            className={`${
                              active ? 'ri-check-line' : 'ri-building-line'
                            } w-4 h-4 flex items-center justify-center shrink-0`}
                          />
                          <span className="flex-1 text-left truncate">{o.name}</span>
                        </button>
                        {isOwner && (
                          <div className="flex items-center gap-0.5 pr-2 shrink-0">
                            <button
                              onClick={() => startRename(o)}
                              title="Rename office"
                              className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer ${
                                active
                                  ? 'text-white hover:bg-white/15'
                                  : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'
                              }`}
                            >
                              <i className="ri-edit-line w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={() => {
                                setDeleting(o);
                                setDeleteErr('');
                              }}
                              title="Delete office"
                              className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer ${
                                active
                                  ? 'text-white hover:bg-white/15'
                                  : 'text-slate-500 hover:bg-slate-200 hover:text-red-600'
                              }`}
                            >
                              <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {canCreateOffice && (
              <div className="border-t border-slate-100 mt-1 pt-1">
                {creating ? (
                  <div className="px-2 pb-1">
                    <input
                      autoFocus
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setErr('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitCreate();
                        if (e.key === 'Escape') {
                          setCreating(false);
                          setName('');
                        }
                      }}
                      placeholder="New office name"
                      className="w-full text-sm px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
                    />
                    {err && <div className="text-[11px] text-red-600 mt-1">{err}</div>}
                    <div className="flex gap-2 mt-1.5">
                      <button
                        onClick={submitCreate}
                        disabled={busy}
                        className="flex-1 px-2 py-1.5 text-xs rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {busy ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        onClick={() => {
                          setCreating(false);
                          setName('');
                          setErr('');
                        }}
                        className="px-2 py-1.5 text-xs rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setCreating(true);
                      setErr('');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 cursor-pointer whitespace-nowrap transition"
                  >
                    <i className="ri-add-line w-4 h-4 flex items-center justify-center text-slate-500" />
                    <span>New office</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setDeleting(null);
              setDeleteErr('');
            }}
          />
          <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-alert-line w-5 h-5 flex items-center justify-center text-red-600" />
              <h3 className="font-semibold text-base">Delete office</h3>
            </div>
            <p className="text-sm text-slate-500 mt-2 mb-4">
              Are you sure you want to delete{' '}
              <span className="font-medium text-slate-700">{deleting.name}</span>? This cannot be
              undone.
            </p>
            {deleteErr && (
              <div className="mb-3 px-3 py-2 rounded-md text-xs bg-red-50 text-red-700 border border-red-200">
                {deleteErr}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={confirmDelete}
                disabled={busy}
                className="flex-1 px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? (
                  <>
                    <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />{' '}
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
              <button
                onClick={() => {
                  setDeleting(null);
                  setDeleteErr('');
                }}
                className="px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}